import { useEffect, useMemo, useRef, useState } from 'react';
import type { Repositories } from '@core/db';
import { telemetry } from '@core/telemetry';
import { entriesOf, type SearchState } from './types';

/** Ищем от двух букв: с одной находится всё подряд, и выдача бесполезна. */
const MIN_QUERY = 2;

/**
 * Данные глобального поиска.
 *
 * Запрос уходит в базу **на каждое изменение строки**, без задержки: на объёмах
 * персональной практики `LIKE` по четырём таблицам занимает единицы миллисекунд,
 * а задержка в полсекунды на телефоне читается как «приложение думает».
 */
export function useSearch(db: Repositories, now: () => number = Date.now) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>({
    query: '',
    entries: [],
    isRecent: true,
    isTooShort: false,
  });

  useEffect(() => {
    let dropped = false;
    const trimmed = query.trim();

    void (async () => {
      // До первого символа — недавнее: пустой экран поиска это потерянное касание.
      if (trimmed.length === 0) {
        const recent = await db.search.recent();
        if (dropped) return;

        setState({ query, entries: entriesOf(recent, now()), isRecent: true, isTooShort: false });
        return;
      }

      if (trimmed.length < MIN_QUERY) {
        setState({ query, entries: [], isRecent: false, isTooShort: true });
        return;
      }

      const found = await db.search.search(trimmed);
      if (dropped) return;

      setState({ query, entries: entriesOf(found, now()), isRecent: false, isTooShort: false });
    })();

    return () => {
      dropped = true;
    };
  }, [db, query, now]);

  /** Идёт ли сейчас начатый запрос: чтобы посчитать поиск один раз, а не на каждую букву. */
  const started = useRef(false);

  const actions = useMemo(
    () => ({
      setQuery(value: string) {
        const empty = value.trim().length === 0;

        if (!empty && !started.current) {
          telemetry.record('searchUsed');
          started.current = true;
        }
        if (empty) started.current = false;

        setQuery(value);
      },

      /**
       * Юрист открыл найденное.
       *
       * Разница между «искал» и «открыл найденное» и есть ответ, работает поиск или
       * человек уходит из него ни с чем. Считается здесь, а не в экране: экран поиска
       * не знает, куда ведёт результат, — это решает каркас.
       */
      resultOpened() {
        telemetry.record('searchResultOpened');
      },
    }),
    [],
  );

  return { state, actions };
}

export type SearchActions = ReturnType<typeof useSearch>['actions'];
